"use client";

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  forwardRef,
} from "react";
import type { NotificationConfig, NotificationContextType } from "./types";

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined,
);

/**
 * 通知Provider组件
 * 提供全局通知管理功能
 */
export const NotificationProvider = forwardRef<
  HTMLDivElement,
  { children: React.ReactNode }
>(({ children }, _ref) => {
  const [currentNotification, setCurrentNotification] =
    useState<NotificationConfig | null>(null);
  const [notificationQueue, setNotificationQueue] = useState<
    NotificationConfig[]
  >([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const isHoveringRef = useRef<boolean>(false);
  const timerExpiredRef = useRef<boolean>(false);

  /**
   * 清除自动关闭定时器
   */
  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  /**
   * 关闭当前通知
   */
  const closeNotification = useCallback(() => {
    clearTimer();
    setCurrentNotification(null);
    timerExpiredRef.current = false;
  }, [clearTimer]);

  /**
   * 设置hover状态
   */
  const setIsHovering = useCallback(
    (hovering: boolean) => {
      isHoveringRef.current = hovering;

      // 如果不再hover且计时器已过期，则关闭通知
      if (!hovering && timerExpiredRef.current) {
        closeNotification();
      }
    },
    [closeNotification],
  );

  /**
   * 显示通知
   */
  const showNotification = useCallback((config: NotificationConfig) => {
    const notificationWithId = {
      ...config,
      id: config.id || `notification-${Date.now()}`,
      autoClose: config.autoClose !== false,
      duration: config.duration || 10000,
    };

    // 如果当前有通知正在显示，添加到队列
    // if (currentNotification) {
    //   setNotificationQueue((prev) => [...prev, notificationWithId]);
    // } else {
    //   setCurrentNotification(notificationWithId);
    // }
    setCurrentNotification(notificationWithId);
  }, []);

  /**
   * 处理通知队列
   */
  useEffect(() => {
    if (!currentNotification && notificationQueue.length > 0) {
      const [nextNotification, ...rest] = notificationQueue;
      setNotificationQueue(rest);
      setCurrentNotification(nextNotification);
    }
  }, [currentNotification, notificationQueue]);

  /**
   * 处理自动关闭
   */
  useEffect(() => {
    if (currentNotification && currentNotification.autoClose) {
      clearTimer();
      timerExpiredRef.current = false;

      timerRef.current = setTimeout(() => {
        timerExpiredRef.current = true;

        // 如果当前没有hover，则关闭通知
        if (!isHoveringRef.current) {
          closeNotification();
        }
        // 如果正在hover，timerExpiredRef 已设为 true，
        // 等 hover 结束时会在 setIsHovering 中触发关闭
      }, currentNotification.duration);
    }

    return () => {
      clearTimer();
      timerExpiredRef.current = false;
    };
  }, [currentNotification, closeNotification, clearTimer]);

  const value: NotificationContextType = {
    showNotification,
    closeNotification,
    currentNotification,
    setIsHovering,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
});

NotificationProvider.displayName = "NotificationProvider";

/**
 * 使用通知Context的Hook
 */
export const useNotificationContext = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error(
      "useNotificationContext must be used within a NotificationProvider",
    );
  }
  return context;
};
